import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import FingerPrintJS from '@fingerprintjs/fingerprintjs';
import toast from 'react-hot-toast';
import QuestionCreate from '../components/QuestionCreate';
import { questionApi } from '../api/question.api';
import type { CreateQuestionRequest, UpdateQuestionRequest } from '../types/Question.api.type';

export default function QuestionEditPage() {
  const { questionId } = useParams<{ questionId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [step, setStep] = useState<number>(1);
  const [isDisplayingLoader, setIsDisplayingLoader] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [editParam, setEditParam] = useState<CreateQuestionRequest>({
    title: '',
    description: '',
    options: [],
    categoryId: '',
    isAnonymous: false,
    expiresAt: undefined,
    presetDuration: 24,
    memberOnly: false,
  });

  // 기존 설문 데이터를 저장 (옵션 ID 매핑용)
  const [originalOptionIds, setOriginalOptionIds] = useState<number[]>([]);

  // 기존 설문 데이터 fetch
  const { data: questionDetail, isLoading } = useQuery({
    queryKey: ['questionDetail', questionId],
    queryFn: async () => {
      const fp = await FingerPrintJS.load();
      const result = await fp.get();
      const response = await questionApi.getQuestionDetail(questionId!, result.visitorId);
      return response.data?.results ?? response.data;
    },
    enabled: !!questionId,
  });

  // 설문 데이터로 폼 프리필
  useEffect(() => {
    if (questionDetail) {
      setEditParam({
        title: '',
        description: questionDetail.questionTitle || questionDetail.description || '',
        questionExtraContent: questionDetail.questionContent || questionDetail.questionExtraContent || '',
        options: questionDetail.questionOptions?.map((opt: { optionText: string }) => opt.optionText) || [],
        categoryId: questionDetail.categoryId?.toString() || '',
        isAnonymous: false,
        expiresAt: undefined,
        presetDuration: 24,
        memberOnly: questionDetail.memberOnly || false,
      });
      setSelectedCategory(questionDetail.categoryId?.toString() || '');
      setOriginalOptionIds(questionDetail.questionOptions?.map((opt: { id: number }) => Number(opt.id)) || []);
    }
  }, [questionDetail]);

  const isContentStepValid = useMemo(() => {
    const { description, options } = editParam;
    const trimmedDescription = description.trim();
    if (trimmedDescription.length < 5 || trimmedDescription.length > 500) return false;
    if (options.length < 2 || options.length > 5) return false;
    const hasInvalidOption = options.some(
      (option) => option.trim().length === 0 || option.trim().length > 100,
    );
    if (hasInvalidOption) return false;
    return true;
  }, [editParam]);

  // 설문 수정 mutation
  const { mutateAsync: updateQuestionMutation } = useMutation({
    mutationFn: () => {
      const updateRequest: UpdateQuestionRequest = {
        description: editParam.description,
        questionExtraContent: editParam.questionExtraContent,
        categoryId: Number(editParam.categoryId),
        options: editParam.options.map((optionText, index) => ({
          id: originalOptionIds[index],
          optionText,
        })),
        presetDuration: editParam.presetDuration,
        memberOnly: editParam.memberOnly,
      };
      return questionApi.updateQuestion(questionId!, updateRequest);
    },
  });

  const setEditParamHandler = (
    key: keyof CreateQuestionRequest,
    value: string | string[] | number | boolean,
  ) => {
    setEditParam((prev) => ({ ...prev, [key]: value }));
  };

  // edit mode: 3단계 (카테고리 → 내용 → 기간)
  const handleNext = () => {
    if (step < 3) setStep((prev) => prev + 1);
  };
  const handlePrev = () => {
    if (step > 1) setStep((prev) => prev - 1);
    else navigate(-1);
  };
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setEditParamHandler('categoryId', category);
  };
  const handleSubmit = async () => {
    setIsDisplayingLoader(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      await Promise.all([updateQuestionMutation(), minDelay]);
      toast.success('설문이 수정되었어요.');
      await queryClient.invalidateQueries({ queryKey: ['questionDetail', questionId] });
      await queryClient.invalidateQueries({ queryKey: ['questionList'] });
      navigate(`/question/info/${questionId}`);
    } catch (error) {
      console.error(error);
      toast.error('설문 수정에 실패했어요. 다시 시도해주세요.');
    } finally {
      setIsDisplayingLoader(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-lg font-medium">설문 정보를 불러오는 중이에요...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isDisplayingLoader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-lg font-medium">설문을 수정 중이에요...</p>
          </div>
        </div>
      )}
      <QuestionCreate
        selectedCategory={selectedCategory}
        handleCategoryChange={handleCategoryChange}
        step={step}
        handleNext={handleNext}
        handlePrev={handlePrev}
        handleSubmit={handleSubmit}
        createQuestionParam={editParam}
        setCreateQuestionParamHandler={setEditParamHandler}
        isContentStepValid={isContentStepValid}
        mode="edit"
      />
    </div>
  );
}
